import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Mail, MessageSquare, Bell, Info, Send, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../../lib/utils';

const AdminSettings = () => {
  const [notificationsLog, setNotificationsLog] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smsStatus, setSmsStatus] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load each API separately to handle individual failures
      try {
        const notifRes = await adminAPI.getNotificationsLog({ limit: 100 });
        setNotificationsLog(notifRes.data);
      } catch (e) {
        console.error('Failed to load notifications log:', e);
      }
      
      try {
        const auditRes = await adminAPI.getAuditLog({ limit: 100 });
        setAuditLog(auditRes.data);
      } catch (e) {
        console.error('Failed to load audit log:', e);
      }
      
      try {
        const smsRes = await adminAPI.getSmsStatus();
        setSmsStatus(smsRes.data);
      } catch (e) {
        console.error('Failed to load SMS status:', e);
        setSmsStatus({ configured: false });
      }
    } catch (error) {
      toast.error('Failed to load some data');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSms = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number');
      return;
    }
    setSendingTest(true);
    try {
      const res = await adminAPI.testSms(testPhone);
      toast.success(res.data.message);
      setTestPhone('');
      loadData(); // Refresh logs
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <AdminLayout title="Settings" subtitle="System configuration and logs">
      <Tabs defaultValue="integrations" className="space-y-4">
        <TabsList>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notification Log</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Resend - LIVE */}
            <Card className="border-green-200">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Resend (Email)</CardTitle>
                    <CardDescription>Transactional email service</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    LIVE
                  </Badge>
                  <span className="text-sm text-green-600">Emails are being sent</span>
                </div>
                <div className="space-y-2">
                  <Label>Sender Email</Label>
                  <Input value="support@muoov.com" disabled className="bg-slate-50" />
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700">
                    ✓ Email integration is active. All transactional emails are being delivered.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Twilio - LIVE */}
            <Card className={smsStatus?.configured ? "border-green-200" : ""}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    smsStatus?.configured ? 'bg-green-100' : 'bg-rose-100'
                  }`}>
                    <MessageSquare className={`w-5 h-5 ${
                      smsStatus?.configured ? 'text-green-600' : 'text-rose-600'
                    }`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Twilio (SMS)</CardTitle>
                    <CardDescription>SMS notifications for guides</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  {smsStatus?.configured ? (
                    <>
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        LIVE
                      </Badge>
                      <span className="text-sm text-green-600">SMS notifications active</span>
                    </>
                  ) : (
                    <>
                      <Badge className="badge-warning">NOT CONFIGURED</Badge>
                      <span className="text-sm text-amber-600">SMS not available</span>
                    </>
                  )}
                </div>
                
                {smsStatus?.configured && (
                  <>
                    <div className="space-y-2">
                      <Label>From Number</Label>
                      <Input value={smsStatus?.phone_number || ''} disabled className="bg-slate-50" />
                    </div>
                    
                    <div className="border-t pt-4 mt-4">
                      <Label className="mb-2 block">Send Test SMS</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="+1234567890"
                          value={testPhone}
                          onChange={(e) => setTestPhone(e.target.value)}
                          data-testid="test-sms-phone"
                        />
                        <Button 
                          onClick={handleTestSms} 
                          disabled={sendingTest}
                          data-testid="send-test-sms-btn"
                        >
                          {sendingTest ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-1" />
                              Test
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-700">
                        ✓ Guides will receive SMS notifications for new tour requests.
                      </p>
                    </div>
                  </>
                )}
                
                {!smsStatus?.configured && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex gap-2">
                      <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                      <p className="text-sm text-amber-700">
                        Configure Twilio credentials to enable SMS notifications.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Log</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notificationsLog.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {log.channel === 'email' && <Mail className="w-3 h-3 mr-1" />}
                            {log.channel === 'sms' && <MessageSquare className="w-3 h-3 mr-1" />}
                            {log.channel === 'whatsapp' && <Bell className="w-3 h-3 mr-1" />}
                            {log.channel}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{log.to_address}</TableCell>
                        <TableCell>{log.template_key}</TableCell>
                        <TableCell>
                          <Badge className={log.status === 'sent' ? 'badge-success' : 'badge-error'}>
                            {log.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {notificationsLog.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No notifications logged yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Admin Audit Log</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8"><div className="spinner" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Entity</TableHead>
                      <TableHead>Admin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLog.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{log.action}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize">{log.entity_type}</span>
                          <span className="text-slate-400 ml-1 text-xs">({log.entity_id.slice(0, 8)}...)</span>
                        </TableCell>
                        <TableCell className="text-slate-500">{log.admin_id.slice(0, 8)}...</TableCell>
                      </TableRow>
                    ))}
                    {auditLog.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                          No audit entries yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminSettings;
